import { useEffect, type RefObject } from 'react';

/**
 * One dismissal contract, for everything that opens over the page.
 *
 * `MenuSurface` has had this since Phase 60 and its own comment says why it is worth having once:
 * *"dismissal contracts is how one of them ends up not closing on Escape"*. The filter panel is
 * exactly that — it opened on a click and closed only by clicking the same button again. Escape did
 * nothing, an outside click did nothing, and the `?` sheet promised "Esc — Close whatever is open".
 * Two personas reported it; the manager showed with a hit test that the open panel sits on top of
 * the right-hand edge of nine rows, so their menus and due-pills are unreachable while it is up.
 *
 * `swallowClick` is opt-in because the two cases genuinely differ. A row menu should let the next
 * click land — clicking another row's `•••` ought to open that row's menu. A panel that *covers*
 * the list should not, because the minimalist found the dismissing click falling through and
 * opening the task underneath, which is worse than nothing happening.
 */
/*
  Escape closes the innermost surface, not every surface.

  The goal picker opens *inside* the filter panel, and with both listening on `document` one
  Escape dismissed both — so closing a picker took the panel it lives in with it. A stack in
  registration order gives the behaviour everyone expects from nested overlays: last opened, first
  closed. Only Escape needs it; a pointer outside both really does mean "close both", and each
  surface can answer that on its own by asking whether the target is inside it.
*/
const openSurfaces: symbol[] = [];

/**
 * Is anything open over the page right now?
 *
 * For a handler on `document` that wants Escape only when Escape means nothing else — the task
 * list's and the board's, which clear a multi-selection with it. One press used to close the
 * bulk-edit popover *and* throw away the batch behind it: backing out of a menu is the ordinary
 * way to change your mind about opening it, and it discarded twelve rows that had taken twelve
 * clicks to assemble, with nothing saying so and nothing bringing them back.
 *
 * Two questions, because there are two kinds of surface and neither sees the other. The stack
 * above holds everything built on `useDismiss` — row menus, the filter panel, the bulk-edit
 * popover — and answers honestly even for a panel with no ARIA role of its own. The DOM query is
 * for the pickers that handle Escape themselves (`Select`, `MultiSelect`) and never register: they
 * are matched by whether they are *rendered*, not by whether they exist, because `Select` keeps
 * its listbox in the DOM behind `hidden` and a query for the role alone matches a closed one.
 *
 * `TaskDetail` deliberately does not use this. It owns Escape ahead of the list by listening in
 * the capture phase and stopping the event, so by the time a bubble handler runs the panel has
 * already answered — its own guard is about what is open above *the panel*, which is a different
 * question with a different answer.
 */
export function hasOpenSurface(): boolean {
  if (openSurfaces.length > 0) return true;
  return Array.from(
    document.querySelectorAll('[role="menu"], [role="listbox"], [role="dialog"], .search-panel'),
  ).some((el) => el.getClientRects().length > 0);
}

export function useDismiss(
  open: boolean,
  onClose: () => void,
  /** Everything that counts as "inside": the surface itself, and the control that opened it. */
  inside: RefObject<HTMLElement | null>[],
  opts: { swallowClick?: boolean; onScroll?: boolean } = {},
): void {
  const { swallowClick = false, onScroll = false } = opts;

  useEffect(() => {
    if (!open) return;

    const me = Symbol('dismissable');
    openSurfaces.push(me);

    const isInside = (target: Node | null) =>
      inside.some((ref) => ref.current?.contains(target) === true);

    const onPointerDown = (event: PointerEvent) => {
      if (isInside(event.target as Node)) return;
      onClose();
      if (!swallowClick) return;
      /*
        `pointerdown` fires before `click`, so closing here still lets the click reach whatever was
        underneath. One capturing listener, armed for exactly one event, stops that — and removes
        itself either way so it cannot eat an unrelated click later.
      */
      const swallow = (click: MouseEvent) => {
        click.stopPropagation();
        click.preventDefault();
      };
      document.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 0);
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (openSurfaces[openSurfaces.length - 1] !== me) return;
      onClose();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    const scrolled = () => onClose();
    if (onScroll) window.addEventListener('scroll', scrolled, true);

    return () => {
      const at = openSurfaces.indexOf(me);
      if (at !== -1) openSurfaces.splice(at, 1);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      if (onScroll) window.removeEventListener('scroll', scrolled, true);
    };
    /*
      `inside` is deliberately not a dependency: it is a fresh array on every render, the refs in it
      are stable, and the effect only reads them when an event fires.
    */
  }, [open, onClose, swallowClick, onScroll]);
}
